import { Action } from '@ngrx/store';
import { Reference } from '../../utils/action-helper.util';
import {
  AddEmailPayload,
  ApplicationData,
  ApplicationTemplatePayload,
  CreateApplicationPayload,
  DomainPayload,
  UserApplication,
  UserEmail,
} from '../_models/application.models';
import { Error } from '../_models/auth.models';
import { RequestCompanyDatatable } from '../_models/company.model';
import { Datatable } from '../_models/report.models';
import {
  ApplicationMetric,
  UpdateAuditLogStatusPayload,
  UpdateViewSettingsPayload,
} from '../_models/settings.models';
import { Template } from '../_models/template.model';

/**
 * Application action types
 */

export enum ApplicationActionTypes {
  LOAD_USER_APPLICATIONS = '[Application] Applications',
  LOAD_USER_APPLICATIONS_SUCCESS = '[Application] Applications Success',
  LOAD_USER_APPLICATIONS_FAILED = '[Application] Applications Failed',
  CREATE_APPLICATION = '[Application] Create Application',
  CREATE_APPLICATION_SUCCESS = '[Application] Create Application Success',
  CREATE_APPLICATION_FAILED = '[Application] Create Application Failed',
  LOAD_APPLICATION = '[Application] Load Application',
  LOAD_APPLICATION_SUCCESS = '[Application] Load Application Success',
  LOAD_APPLICATION_FAILED = '[Application] Load Application Failed',
  LOAD_APPLICATION_BY_ID = '[Application] Load Application By Id',
  LOAD_APPLICATION_BY_ID_SUCCESS = '[Application] Load Application By Id Success',
  LOAD_APPLICATION_BY_ID_FAILED = '[Application] Load Application By Id Failed',
  UPDATE_APPLICATION = '[Application] Update Application',
  UPDATE_APPLICATION_SUCCESS = '[Application] Update Application Success',
  UPDATE_APPLICATION_FAILED = '[Application] Update Application Failed',
  LOAD_EMAILS = '[Application] Load Emails',
  LOAD_EMAILS_SUCCESS = '[Application] Load Emails Success',
  LOAD_EMAILS_FAILED = '[Application] Load Emails Failed',
  ADD_EMAIL = '[Application] Add Email',
  ADD_EMAIL_SUCCESS = '[Application] Add Email Success',
  ADD_EMAIL_FAILED = '[Application] Add Email Failed',
  LOAD_IP_TRACKING_DATATABLE = '[Application] Load Ip Tracking Datatable',
  LOAD_IP_TRACKING_DATATABLE_SUCCESS = '[Application] Load Ip Tracking Datatable Success',
  LOAD_IP_TRACKING_DATATABLE_FAILED = '[Application] Load Ip Tracking Datatable Failed',
  GET_APP_METRICS = '[Application] Get Application Metrics',
  GET_APP_METRICS_SUCCESS = '[Application] Get Application Metrics Success',
  GET_APP_METRICS_FAILED = '[Application] Get Application Metrics Failed',
  CLEAR_APPS = '[Application] Clear Apps',
  LOAD_APPLICATIONS_USERS_DATATABLE = '[Application] Load Application Users Datatable',
  LOAD_APPLICATIONS_USERS_DATATABLE_SUCCESS = '[Application] Load Application Users Datatable Success',
  LOAD_APPLICATIONS_USERS_DATATABLE_FAILED = '[Application] Load Application Users Datatable Failed',
  RESET = '[Application] RESET',
  LOAD_APPLICATION_TEMPLATE = '[Application] Load Application Template',
  LOAD_APPLICATION_TEMPLATE_SUCCESS = '[Application] Load Application Template Success',
  LOAD_APPLICATION_TEMPLATE_FAILED = '[Application] Load Application Template Failed',
  LOAD_APPLICATION_TEMPLATES = '[Application] Load Application Templates',
  LOAD_APPLICATION_DOC_TEMPLATES = '[Application] Load Application Doc Templates',
  LOAD_APPLICATION_DOC_TEMPLATES_SUCCESS = '[Application] Load Application Doc Templates Success',
  LOAD_APPLICATION_DOC_TEMPLATES_FAILED = '[Application] Load Application Doc Templates Failed',
  LOAD_APPLICATION_TEMPLATES_SUCCESS = '[Application] Load Application Templates Success',
  LOAD_APPLICATION_TEMPLATES_FAILED = '[Application] Load Application Templates Failed',
  CREATE_APPLICATION_TEMPLATE = '[Application] Create Application Template',
  CREATE_APPLICATION_TEMPLATE_SUCCESS = '[Application] Create Application Template Success',
  CREATE_APPLICATION_TEMPLATE_FAILED = '[Application] Create Application Template Failed',
  UPDATE_APPLICATION_TEMPLATE = '[Application] Update Application Template',
  UPDATE_APPLICATION_TEMPLATE_SUCCESS = '[Application] Update Application Template Success',
  UPDATE_APPLICATION_TEMPLATE_FAILED = '[Application] Update Application Template Failed',
  DELETE_APPLICATION_TEMPLATE = '[Application] Delete Application Template',
  DELETE_APPLICATION_TEMPLATE_SUCCESS = '[Field] Delete Application Template Success',
  DELETE_APPLICATION_TEMPLATE_FAILED = '[Field] Delete Application Template Failed',
  CREATE_DOMAIN = '[Application] Create Domain',
  CREATE_DOMAIN_SUCCESS = '[Field] Create Domain Success',
  CREATE_DOMAIN_FAILED = '[Field] Create Domain Failed',
  CHECK_DOMAIN_STATUS = '[Application] Check Domain Status',
  CHECK_DOMAIN_STATUS_SUCCESS = '[Field] Check Domain Status Success',
  CHECK_DOMAIN_STATUS_FAILED = '[Field] Check Domain Status Failed',
  DELETE_APPLICATION = '[Application] Delete Application',
  DELETE_APPLICATION_SUCCESS = '[Application] Delete Application Success',
  DELETE_APPLICATION_FAILED = '[Application] Delete Application Failed',
  GET_TIME_ZONES = '[Application] Get Timezones',
  GET_TIME_ZONES_SUCCESS = '[Application] Get Timezones Success',
  GET_TIME_ZONES_FAILED = '[Application] Get Timezones Failed',
  GET_LANGUAGES = '[Application] Get Languages',
  GET_LANGUAGES_SUCCESS = '[Application] Get Languages Success',
  GET_LANGUAGES_FAILED = '[Application] Get Languages Failed',
  RENAME_APPLICATION = '[Application] Rename Application',
  RENAME_APPLICATION_SUCCESS = '[Application] Rename Application Success',
  RENAME_APPLICATION_FAILED = '[Application] Rename Application Failed',
  LOAD_SUBDOMAINS = '[Application] Load Subdomains',
  LOAD_SUBDOMAINS_SUCCESS = '[Application] Load Subdomains Success',
  LOAD_SUBDOMAINS_FAILED = '[Application] Load Subdomains Failed',
}

/**
 * Operations
 */
export class LoadUserApplications implements Action {
  readonly type = ApplicationActionTypes.LOAD_USER_APPLICATIONS;

  constructor(public params: object = {}) {}
}

export class LoadUserApplicationsSuccess implements Action {
  readonly type = ApplicationActionTypes.LOAD_USER_APPLICATIONS_SUCCESS;

  constructor(public payload: Array<UserApplication>) {}
}

export class LoadUserApplicationsFailed implements Action {
  readonly type = ApplicationActionTypes.LOAD_USER_APPLICATIONS_FAILED;

  constructor(public payload: Error) {}
}

export class GetAppMetrics implements Action {
  readonly type = ApplicationActionTypes.GET_APP_METRICS;

  constructor(public applicationId: string) {}
}

export class GetAppMetricsSuccess implements Action {
  readonly type = ApplicationActionTypes.GET_APP_METRICS_SUCCESS;

  constructor(
    public applicationId: string,
    public payload: ApplicationMetric
  ) {}
}

export class GetAppMetricsFailed implements Action {
  readonly type = ApplicationActionTypes.GET_APP_METRICS_FAILED;

  constructor(public payload: Error) {}
}

export class CreateApplicationTemplate extends Reference implements Action {
  readonly type = ApplicationActionTypes.CREATE_APPLICATION_TEMPLATE;

  constructor(public payload: Template) {
    super();
  }
}

export class CreateApplicationTemplateSuccess extends Reference implements Action {
  readonly type = ApplicationActionTypes.CREATE_APPLICATION_TEMPLATE_SUCCESS;

  constructor(public payload: ApplicationTemplatePayload) {
    super();
  }
}

export class CreateApplicationTemplateFailed extends Reference implements Action {
  readonly type = ApplicationActionTypes.CREATE_APPLICATION_TEMPLATE_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class CreateApplication extends Reference implements Action {
  readonly type = ApplicationActionTypes.CREATE_APPLICATION;

  constructor(public payload: CreateApplicationPayload) {
    super();
  }
}

export class CreateApplicationSuccess extends Reference implements Action {
  readonly type = ApplicationActionTypes.CREATE_APPLICATION_SUCCESS;

  constructor(public payload: UserApplication) {
    super();
  }
}

export class CreateApplicationFailed extends Reference implements Action {
  readonly type = ApplicationActionTypes.CREATE_APPLICATION_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class LoadApplication implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION;

  constructor(public tableId: string) {}
}

export class LoadApplicationSuccess implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_SUCCESS;

  constructor(public tableId: string, public payload: ApplicationData) {}
}

export class LoadApplicationFailed implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_FAILED;

  constructor(public payload: Error, public tableId: string) {}
}

export class LoadApplicationDocTemplates implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_DOC_TEMPLATES;

  constructor(public appId: string, public force: boolean = false,public geoJsonTemplate:boolean = false) {}
}

export class LoadApplicationDocTemplatesSuccess implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_DOC_TEMPLATES_SUCCESS;

  constructor(public appId: string, public payload: Array<ApplicationTemplatePayload>) {}
}

export class LoadApplicationDocTemplatesFailed implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_DOC_TEMPLATES_FAILED;

  constructor(public payload: Error, public appId: string) {}
}

export class LoadApplicationTemplates implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_TEMPLATES;

  constructor(public appId: string, public docTemplate: boolean =false, public geoJson: boolean =false, public force: boolean = false) {}
}

export class LoadApplicationTemplatesSuccess implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_TEMPLATES_SUCCESS;

  constructor(public appId: string, public docTemplate: boolean =false, public geoJson: boolean =false, public payload: Array<ApplicationTemplatePayload>) {}
}

export class LoadApplicationTemplatesFailed implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_TEMPLATES_FAILED;

  constructor(public payload: Error, public appId: string) {}
}

export class LoadApplicationTemplate implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_TEMPLATE;

  constructor(public id: string) {}
}

export class LoadApplicationTemplateSuccess implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_TEMPLATE_SUCCESS;

  constructor(public id: string, public payload: ApplicationTemplatePayload) {}
}

export class LoadApplicationTemplateFailed implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_TEMPLATE_FAILED;

  constructor(public payload: Error) {}
}

export class UpdateApplicationTemplate extends Reference implements Action {
  readonly type = ApplicationActionTypes.UPDATE_APPLICATION_TEMPLATE;

  constructor(public payload: Template) {
    super();
  }
}

export class UpdateApplicationTemplateSuccess extends Reference implements Action {
  readonly type = ApplicationActionTypes.UPDATE_APPLICATION_TEMPLATE_SUCCESS;

  constructor(public payload: ApplicationTemplatePayload) {
    super();
  }
}

export class UpdateApplicationTemplateFailed extends Reference implements Action {
  readonly type = ApplicationActionTypes.UPDATE_APPLICATION_TEMPLATE_FAILED;

  constructor(public payload: any) {
    super();
  }
}

export class DeleteApplicationTemplate extends Reference implements Action {
  readonly type = ApplicationActionTypes.DELETE_APPLICATION_TEMPLATE;

  constructor(public appId: string, public key: string) {
    super();
  }
}

export class DeleteApplicationTemplateSuccess extends Reference implements Action {
  readonly type = ApplicationActionTypes.DELETE_APPLICATION_TEMPLATE_SUCCESS;

  constructor(public appId: string, public key: string) {
    super();
  }
}

export class DeleteApplicationTemplateFailed extends Reference implements Action {
  readonly type = ApplicationActionTypes.DELETE_APPLICATION_TEMPLATE_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class UpdateApplication implements Action {
  readonly type = ApplicationActionTypes.UPDATE_APPLICATION;

  constructor(
    public payload:
      | UpdateAuditLogStatusPayload
      | UpdateViewSettingsPayload
      | UserApplication
  ) {}
}

export class UpdateApplicationSuccess implements Action {
  readonly type = ApplicationActionTypes.UPDATE_APPLICATION_SUCCESS;

  constructor(public payload: UserApplication) {}
}

export class UpdateApplicationFailed implements Action {
  readonly type = ApplicationActionTypes.UPDATE_APPLICATION_FAILED;

  constructor(public payload: any) {}
}

export class LoadApplicationById implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_BY_ID;

  constructor(public appId: string, public fromSettings: boolean = false) {}
}

export class LoadApplicationByIdSuccess implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_BY_ID_SUCCESS;

  constructor(public appId: string, public payload: UserApplication) {}
}

export class LoadApplicationByIdFailed implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATION_BY_ID_FAILED;

  constructor(public payload: any) {}
}

export class LoadEmails implements Action {
  readonly type = ApplicationActionTypes.LOAD_EMAILS;

  constructor(
    public applicationId: string,
    public includeDefault: boolean = true,
    public force: boolean = false
  ) {}
}

export class LoadEmailsSuccess implements Action {
  readonly type = ApplicationActionTypes.LOAD_EMAILS_SUCCESS;

  constructor(public applicationId: string, public payload: Array<UserEmail>) {}
}

export class LoadEmailsFailed implements Action {
  readonly type = ApplicationActionTypes.LOAD_EMAILS_FAILED;

  constructor(public payload: Error, public applicationId: string) {}
}

export class AddEmail extends Reference implements Action {
  readonly type = ApplicationActionTypes.ADD_EMAIL;

  constructor(public appId: string, public payload: AddEmailPayload) {
    super();
  }
}

export class AddEmailSuccess extends Reference implements Action {
  readonly type = ApplicationActionTypes.ADD_EMAIL_SUCCESS;

  constructor(public payload: any) {
    super();
  }
}

export class AddEmailFailed extends Reference implements Action {
  readonly type = ApplicationActionTypes.ADD_EMAIL_FAILED;

  constructor(public payload: any) {
    super();
  }
}
export class CreateDomain extends Reference implements Action {
  readonly type = ApplicationActionTypes.CREATE_DOMAIN;

  constructor(public appId: string, public payload: DomainPayload) {
    super();
  }
}

export class CreateDomainSuccess extends Reference implements Action {
  readonly type = ApplicationActionTypes.CREATE_DOMAIN_SUCCESS;

  constructor(public appId: string, public payload: any) {
    super();
  }
}

export class CreateDomainFailed extends Reference implements Action {
  readonly type = ApplicationActionTypes.CREATE_DOMAIN_FAILED;

  constructor(public payload: any) {
    super();
  }
}
export class CheckDomainStatus extends Reference implements Action {
  readonly type = ApplicationActionTypes.CHECK_DOMAIN_STATUS;

  constructor(public appId: string, public payload: any) {
    super();
  }
}

export class CheckDomainStatusSuccess extends Reference implements Action {
  readonly type = ApplicationActionTypes.CHECK_DOMAIN_STATUS_SUCCESS;

  constructor(public appId:string, public payload: any) {
    super();
  }
}

export class CheckDomainStatusFailed extends Reference implements Action {
  readonly type = ApplicationActionTypes.CHECK_DOMAIN_STATUS_FAILED;

  constructor(public payload: any) {
    super();
  }
}

export class LoadIpTrackingDatatable implements Action {
  readonly type = ApplicationActionTypes.LOAD_IP_TRACKING_DATATABLE;

  /**
   * @param force to forcefully fetch, regardless of caching enabled
   */
  constructor(
    public payload: RequestCompanyDatatable,
    public force: boolean = false
  ) {}
}

export class LoadIpTrackingDatatableSuccess implements Action {
  readonly type = ApplicationActionTypes.LOAD_IP_TRACKING_DATATABLE_SUCCESS;

  /**
   * @param start start from (from request payload)
   * @param length page size (from request payload)
   */
  constructor(
    public applicationId: string,
    public start: number,
    public length: number,
    public payload: Datatable
  ) {}
}

export class LoadIpTrackingDatatableFailed implements Action {
  readonly type = ApplicationActionTypes.LOAD_IP_TRACKING_DATATABLE_FAILED;

  constructor(public payload: Error) {}
}

export class ClearApps implements Action {
  readonly type = ApplicationActionTypes.CLEAR_APPS;
}

export class LoadApplicationUsersDatatable implements Action {
  readonly type = ApplicationActionTypes.LOAD_APPLICATIONS_USERS_DATATABLE;

  /**
   * @param force to forcefully fetch, regardless of caching enabled
   */
  constructor(
    public payload: RequestCompanyDatatable,
    public force: boolean = false
  ) {}
}

export class LoadApplicationUsersDatatableSuccess implements Action {
  readonly type =
    ApplicationActionTypes.LOAD_APPLICATIONS_USERS_DATATABLE_SUCCESS;

  /**
   * @param start start from (from request payload)
   * @param length page size (from request payload)
   */
  constructor(
    public applicationId: string,
    public start: number,
    public length: number,
    public payload: Datatable
  ) {}
}

export class LoadApplicationUsersDatatableFailed implements Action {
  readonly type =
    ApplicationActionTypes.LOAD_APPLICATIONS_USERS_DATATABLE_FAILED;

  constructor(public payload: Error) {}
}

export class DeleteApplication extends Reference implements Action {
  readonly type = ApplicationActionTypes.DELETE_APPLICATION;

  constructor(public appId: string) {
    super();
  }
}

export class DeleteApplicationSuccess extends Reference implements Action {
  readonly type = ApplicationActionTypes.DELETE_APPLICATION_SUCCESS;

  constructor(public response: any) {
    super();
  }
}

export class DeleteApplicationFailed extends Reference implements Action {
  readonly type = ApplicationActionTypes.DELETE_APPLICATION_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class GetTimeZones extends Reference implements Action {
  readonly type = ApplicationActionTypes.GET_TIME_ZONES;

  constructor() {
    super();
  }
}

export class GetTimeZonesSuccess extends Reference implements Action {
  readonly type = ApplicationActionTypes.GET_TIME_ZONES_SUCCESS;

  constructor(public payload: any) {
    super();
  }
}

export class GetTimeZonesFailed extends Reference implements Action {
  readonly type = ApplicationActionTypes.GET_TIME_ZONES_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class GetLanguages extends Reference implements Action {
  readonly type = ApplicationActionTypes.GET_LANGUAGES;

  constructor() {
    super();
  }
}

export class GetLanguagesSuccess extends Reference implements Action {
  readonly type = ApplicationActionTypes.GET_LANGUAGES_SUCCESS;

  constructor(public payload: any) {
    super();
  }
}

export class GetLanguagesFailed extends Reference implements Action {
  readonly type = ApplicationActionTypes.GET_LANGUAGES_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class RenameApplication extends Reference implements Action {
  readonly type = ApplicationActionTypes.RENAME_APPLICATION;

  constructor(public payload: any) {
    super();
  }
}

export class RenameApplicationSuccess extends Reference implements Action {
  readonly type = ApplicationActionTypes.RENAME_APPLICATION_SUCCESS;

  constructor(public response: any) {
    super();
  }
}

export class RenameApplicationFailed extends Reference implements Action {
  readonly type = ApplicationActionTypes.RENAME_APPLICATION_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class LoadSubdomains extends Reference implements Action {
  readonly type = ApplicationActionTypes.LOAD_SUBDOMAINS;
  
  constructor() {
    super();
  }
}

export class LoadSubdomainsSuccess extends Reference implements Action {
  readonly type = ApplicationActionTypes.LOAD_SUBDOMAINS_SUCCESS;
  constructor(public payload: any[]) {
    super();
  }
}

export class LoadSubdomainsFailed extends Reference implements Action {
  readonly type = ApplicationActionTypes.LOAD_SUBDOMAINS_FAILED;
  constructor(public payload: Error) {
    super();
  }
}

export class Reset implements Action {
  readonly type = ApplicationActionTypes.RESET;

  constructor() {}
}

/**
 * Available types
 */
export type Actions =
  | LoadUserApplications
  | LoadUserApplicationsSuccess
  | LoadUserApplicationsFailed
  | CreateApplication
  | CreateApplicationSuccess
  | CreateApplicationFailed
  | LoadApplication
  | LoadApplicationSuccess
  | LoadApplicationFailed
  | UpdateApplication
  | UpdateApplicationSuccess
  | UpdateApplicationFailed
  | LoadApplicationById
  | LoadApplicationByIdSuccess
  | LoadApplicationByIdFailed
  | LoadEmails
  | LoadEmailsSuccess
  | LoadEmailsFailed
  | AddEmail
  | AddEmailSuccess
  | AddEmailFailed
  | LoadIpTrackingDatatable
  | LoadIpTrackingDatatableSuccess
  | LoadIpTrackingDatatableFailed
  | ClearApps
  | GetAppMetrics
  | GetAppMetricsSuccess
  | GetAppMetricsFailed
  | LoadApplicationUsersDatatable
  | LoadApplicationUsersDatatableSuccess
  | LoadApplicationUsersDatatableFailed
  | LoadApplicationTemplate
  | LoadApplicationTemplateSuccess
  | LoadApplicationTemplateFailed
  | LoadApplicationDocTemplates
  | LoadApplicationDocTemplatesSuccess
  | LoadApplicationDocTemplatesFailed
  | LoadApplicationTemplates
  | LoadApplicationTemplatesSuccess
  | LoadApplicationTemplatesFailed
  | CreateApplicationTemplate
  | CreateApplicationTemplateSuccess
  | CreateApplicationTemplateFailed
  | UpdateApplicationTemplate
  | UpdateApplicationTemplateSuccess
  | UpdateApplicationTemplateFailed
  | DeleteApplicationTemplate
  | DeleteApplicationTemplateSuccess
  | DeleteApplicationTemplateFailed
  | CreateDomain
  | CreateDomainSuccess
  | CreateDomainFailed
  | CheckDomainStatus
  | CheckDomainStatusSuccess
  | CheckDomainStatusFailed
  | DeleteApplication
  | DeleteApplicationSuccess
  | DeleteApplicationFailed
  | GetTimeZones
  | GetTimeZonesSuccess
  | GetTimeZonesFailed
  | GetLanguages
  | GetLanguagesSuccess
  | GetLanguagesFailed
  | RenameApplication
  | RenameApplicationSuccess
  | RenameApplicationFailed
  | LoadSubdomains
  | LoadSubdomainsSuccess
  | LoadSubdomainsFailed
  | Reset;
